import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const initialFormData = {
    assetName: '',
    brand: '',
    description: '',
    purchaseDate: '',
    warrantyExpiry: '',
    status: 'Active'
};

const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
        return dateString;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const StoreAssetsAdd = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.assetName.trim()) {
            newErrors.assetName = 'Asset name is required';
        }

        if (!formData.brand.trim()) {
            newErrors.brand = 'Brand is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!formData.purchaseDate) {
            newErrors.purchaseDate = 'Purchase date is required';
        }

        if (!formData.warrantyExpiry) {
            newErrors.warrantyExpiry = 'Warranty expiry is required';
        }

        if (
            formData.purchaseDate &&
            formData.warrantyExpiry &&
            new Date(formData.warrantyExpiry) < new Date(formData.purchaseDate)
        ) {
            newErrors.warrantyExpiry = 'Warranty expiry cannot be before purchase date';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fill all required fields');
            return;
        }

        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: 'create-asset',
                assetName: formData.assetName.trim(),
                brand: formData.brand.trim(),
                description: formData.description.trim(),
                purchaseDate: formatDateToDDMMYYYY(formData.purchaseDate),
                warrantyExpiry: formatDateToDDMMYYYY(formData.warrantyExpiry),
                status: formData.status
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                toast.success(result.message || 'Asset added successfully');
                navigate('/store-assets');
            } else {
                toast.error(result?.message || 'Failed to add asset');
            }
        } catch (error) {
            console.error('Error adding store asset:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-8">
                            <h1 className="employee-title">
                                Add Store Asset
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                Add asset details to store and manage inventory availability.
                            </p>
                        </div>
                        <div className="col-md-4 employee-view-header-actions">
                            <button
                                type="button"
                                className="employee-back-btn"
                                onClick={() => navigate('/store-assets')}
                                disabled={loading}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                      {loading ? (
                       <Loading size={120} />
                       ) : (

                    <div className="card employee-content-card">
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <h4 className="section-title">Asset Information</h4>
                                    <p className="section-subtitle">
                                        Fill in the store asset details below.
                                    </p>

                                    <div
                                        className="form-row-3"
                                        style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
                                    >
                                        <div className="form-group">
                                            <label htmlFor="assetName" className="form-label">
                                                Asset Name<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="assetName"
                                                name="assetName"
                                                className={`form-control ${errors.assetName ? 'is-invalid' : ''}`}
                                                value={formData.assetName}
                                                onChange={handleChange}
                                                placeholder="Enter asset name"
                                            />
                                            {errors.assetName && (
                                                <div className="invalid-feedback">{errors.assetName}</div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="brand" className="form-label">
                                                Brand<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="brand"
                                                name="brand"
                                                className={`form-control ${errors.brand ? 'is-invalid' : ''}`}
                                                value={formData.brand}
                                                onChange={handleChange}
                                                placeholder="Enter brand"
                                            />
                                            {errors.brand && (
                                                <div className="invalid-feedback">{errors.brand}</div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="purchaseDate" className="form-label">
                                                Purchase Date<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                id="purchaseDate"
                                                name="purchaseDate"
                                                className={`form-control ${errors.purchaseDate ? 'is-invalid' : ''}`}
                                                value={formData.purchaseDate}
                                                onChange={handleChange}
                                            />
                                            {errors.purchaseDate && (
                                                <div className="invalid-feedback">{errors.purchaseDate}</div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="warrantyExpiry" className="form-label">
                                                Warranty Expiry<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                id="warrantyExpiry"
                                                name="warrantyExpiry"
                                                className={`form-control ${errors.warrantyExpiry ? 'is-invalid' : ''}`}
                                                value={formData.warrantyExpiry}
                                                onChange={handleChange}
                                                min={formData.purchaseDate || undefined}
                                            />
                                            {errors.warrantyExpiry && (
                                                <div className="invalid-feedback">{errors.warrantyExpiry}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                                                       
                                    <div className="form-group">
                                        <label htmlFor="description" className="form-label">
                                            Description<span className="required-star">*</span>
                                        </label>
                                        <textarea
                                            id="description"
                                            name="description"
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            value={formData.description}
                                            onChange={handleChange}
                                            placeholder="Add model, serial number, or any identifying details."
                                            rows="4"
                                        />
                                        {errors.description && (
                                            <div className="invalid-feedback">{errors.description}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => navigate('/store-assets')}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Adding...' : 'Add Asset'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                       )}
                </div>
            </section>
        </div>
    );
};

export default StoreAssetsAdd;